import axios from "axios";
import createAuthRefreshInterceptor, {
  AxiosAuthRefreshRequestConfig,
} from "axios-auth-refresh";
import {
  getStoredAccessToken,
  getStoredRefreshToken,
  storeTokens,
  refreshToken as refreshTokenApi,
} from "../services/AuthService";

// Automatically attach auth token to every request
axios.interceptors.request.use(async (config) => {
  const headers = config.headers as any;
  if (!headers?.Authorization) {
    const token = getStoredAccessToken();
    if (token) {
      headers.Authorization = token;
    }
  }
  return config;
});

interface GetRequest {
  path: string;
  accessToken?: string;
  skipAuthRefresh?: boolean;
  headers?: any;
  setAccessToken?: (token: string) => void;
  isReportService?: boolean;
}

interface PostRequest {
  path: string;
  body: any;
  accessToken?: string;
  headers?: any;
  setAccessToken?: (token: string) => void;
}

const getRequestHeaders = (accessToken?: string): any => {
  const headers: { Authorization?: string } = {};
  if (accessToken) {
    headers.Authorization = accessToken;
  }
  return headers;
};

const refreshAccessToken = (): Promise<any> => {
  const storedRefreshToken = getStoredRefreshToken();
  if (!storedRefreshToken) {
    return Promise.reject(new Error("No refresh token available"));
  }
  return refreshTokenApi(storedRefreshToken).then((response) => {
    const { accessToken, refreshToken } = response.data.data;
    storeTokens(accessToken, refreshToken);
  });
};

const get = ({
  path,
  skipAuthRefresh,
  accessToken,
  headers,
  setAccessToken,
  isReportService,
}: GetRequest) => {
  const url = process.env.REACT_APP_BASE_URL + path;
  console.log("#### url hitting : ", url);
  if (!skipAuthRefresh) {
    createAuthRefreshInterceptor(axios, () => refreshAccessToken());
  }
  return axios.get(url, {
    skipAuthRefresh: skipAuthRefresh,
    headers: {
      ...getRequestHeaders(accessToken),
      headers,
    },
    timeout: 8000,
  } as AxiosAuthRefreshRequestConfig);
};

const publicGet = (url: string, headers: any) => {
  return axios.get(url, { headers });
};

const post = ({
  path,
  accessToken,
  headers,
  setAccessToken,
  body,
}: PostRequest) => {
  const url = process.env.REACT_APP_BASE_URL + path;
  createAuthRefreshInterceptor(axios, () => refreshAccessToken());
  return axios
    .post(url, body, {
      skipAuthRefresh: false,
      headers: {
        ...getRequestHeaders(accessToken),
        ...headers,
      },
    } as AxiosAuthRefreshRequestConfig)
    .catch((error) => {
      return error;
    });
};
const put = ({
  path,
  accessToken,
  headers,
  setAccessToken,
  body,
}: PostRequest) => {
  const url = process.env.REACT_APP_BASE_URL + path;
  createAuthRefreshInterceptor(axios, () => refreshAccessToken());
  return axios.put(url, body, {
    skipAuthRefresh: false,
    headers: {
      ...getRequestHeaders(accessToken),
      headers,
    },
  } as AxiosAuthRefreshRequestConfig);
};

const patch = ({
  path,
  accessToken,
  headers,
  setAccessToken,
  body,
}: PostRequest) => {
  const url = process.env.REACT_APP_BASE_URL + path;
  createAuthRefreshInterceptor(axios, () => refreshAccessToken());
  return axios.patch(url, body, {
    skipAuthRefresh: false,
    headers: {
      ...getRequestHeaders(accessToken),
      headers,
    },
  } as AxiosAuthRefreshRequestConfig);
};

const deleteHttp = ({
  path,
  accessToken,
  headers,
  setAccessToken,
  body,
}: PostRequest) => {
  const url = process.env.REACT_APP_BASE_URL + path;
  createAuthRefreshInterceptor(axios, () => refreshAccessToken());
  return axios.delete(url, {
    skipAuthRefresh: false,
    headers: {
      ...getRequestHeaders(accessToken),
      headers,
    },
    data: body,
  } as AxiosAuthRefreshRequestConfig);
};

// eslint-disable-next-line import/no-anonymous-default-export
export default { get, post, put, patch, deleteHttp, publicGet };
